<script setup lang="ts">
import Menu from 'primevue/menu'
import type { MenuItem } from 'primevue/menuitem'
import { computed, ref, useId } from 'vue'
import { useRouter, type RouteLocationRaw } from 'vue-router'

import AppIcon from './AppIcon.vue'

export interface DropdownAction {
  id: string
  label?: string
  /** Feather icon name. */
  icon?: string
  /** Router link. */
  to?: RouteLocationRaw
  /** External link. */
  href?: string
  target?: '_blank' | '_self'
  disabled?: boolean
  /** Shown on hover — mostly why a `disabled` item cannot be used (06 current workspace, Export at 0 rows). */
  tooltip?: string
  /** Red item (row "Delete"). */
  danger?: boolean
  /** Coloured item (10: "Income" green, "Expense" red); `danger` is the same as `variant: 'danger'`. */
  variant?: 'primary' | 'success' | 'warning' | 'danger' | 'info'
  /** A divider line instead of an item. */
  separator?: boolean
  /**
   * A group header (legacy `b-dropdown-group header`, 12 "Application Status"): a non-clickable title over the items
   * that follow it, up to the next header or separator.
   */
  header?: boolean
  onSelect?: () => void
}

const props = withDefaults(
  defineProps<{
    items: DropdownAction[]
    /** Accessible name of the trigger. */
    label: string
    /** Feather icon of the default trigger (`MoreVerticalIcon` for row menus). */
    icon?: string
    iconSize?: number
    /** `right`: the menu's right edge under the trigger (navbar menus, legacy `right`). */
    align?: 'left' | 'right'
    /**
     * `bottom` (default): under the trigger, flipped above when there is no room. `top`: always above the trigger —
     * legacy `dropup` (12: the bulk "Move To" / "Actions" menus of the applications toolbar).
     */
    placement?: 'bottom' | 'top'
    /**
     * The menu's lowest z-index (PrimeVue `baseZIndex`): over a fixed layer that sits above the overlays — the
     * assistant panel (1030), as the shell's account menu does (`NavAccountCard`, 1100).
     */
    baseZIndex?: number
  }>(),
  { icon: 'MoreVerticalIcon', iconSize: 18, align: 'right', placement: 'bottom', baseZIndex: 0 },
)

const router = useRouter()
const menu = ref<InstanceType<typeof Menu> | null>(null)
const menuId = `dropdown-${useId()}`
const target = ref<HTMLElement | null>(null)

function toMenuItem(item: DropdownAction): MenuItem {
  if (item.separator) return { separator: true, key: item.id }
  const variant = item.variant ?? (item.danger ? 'danger' : undefined)
  return {
    key: item.id,
    label: item.label,
    disabled: item.disabled,
    class: variant ? `app-dropdown__item--${variant}` : undefined,
    action: item,
  }
}

/** Headers become PrimeVue groups: the header's label over the items up to the next header or separator. */
const model = computed<MenuItem[]>(() => {
  const out: MenuItem[] = []
  let group: MenuItem | null = null
  for (const item of props.items) {
    if (item.header) {
      group = { key: item.id, label: item.label, items: [], class: 'app-dropdown__header' }
      out.push(group)
    } else if (item.separator) {
      group = null
      out.push(toMenuItem(item))
    } else if (group) {
      ;(group.items as MenuItem[]).push(toMenuItem(item))
    } else {
      out.push(toMenuItem(item))
    }
  }
  return out
})

function variantOf(item: DropdownAction): string | undefined {
  return item.variant ?? (item.danger ? 'danger' : undefined)
}

function select(item: DropdownAction): void {
  if (item.disabled) return
  menu.value?.hide()
  if (item.onSelect) item.onSelect()
  else if (item.to) void router.push(item.to)
  else if (item.href) window.open(item.href, item.target ?? '_self', item.target === '_blank' ? 'noopener' : undefined)
}

function toggle(event: Event): void {
  target.value = (event.currentTarget as HTMLElement | null) ?? null
  menu.value?.toggle(event)
}

/** `placement="top"`: PrimeVue places the panel under the trigger (or flips it); move it above, legacy `dropup`. */
function onShow(): void {
  if (props.placement !== 'top' || !target.value) return
  const panel = document.getElementById(menuId)
  if (!panel) return
  const rect = target.value.getBoundingClientRect()
  const top = rect.top + window.scrollY - panel.offsetHeight - 2
  panel.style.top = `${Math.max(top, window.scrollY)}px`
  panel.style.transformOrigin = 'bottom'
}
</script>

<template>
  <div class="app-dropdown inline-flex" data-kit="dropdown">
    <slot name="trigger" :toggle="toggle" :menu-id="menuId">
      <button
        type="button"
        class="app-dropdown__trigger"
        :aria-label="label"
        aria-haspopup="true"
        :aria-controls="menuId"
        @click="toggle"
      >
        <AppIcon :name="icon" :size="iconSize" />
      </button>
    </slot>
    <Menu
      :id="menuId"
      ref="menu"
      :model="model"
      popup
      :base-z-index="baseZIndex"
      :pt="{
        root: {
          class: [
            align === 'right' ? 'app-dropdown__menu--right' : '',
            placement === 'top' ? 'app-dropdown__menu--top' : '',
          ],
          'data-placement': placement,
        },
      }"
      @show="onShow"
    >
      <template #submenulabel="{ item }">
        <span class="app-dropdown__header-label" data-part="header">{{ item.label }}</span>
      </template>
      <template #item="{ item, props: itemProps }">
        <a
          v-if="item.action"
          v-bind="itemProps.action"
          :href="(item.action as DropdownAction).href"
          :target="(item.action as DropdownAction).target"
          class="flex items-center gap-2 px-4 py-2"
          :class="{
            'text-danger': (item.action as DropdownAction).danger,
            [`app-dropdown__item--${variantOf(item.action as DropdownAction)}`]: variantOf(
              item.action as DropdownAction,
            ),
          }"
          :data-action="(item.action as DropdownAction).id"
          @click.prevent="select(item.action as DropdownAction)"
        >
          <span
            v-tooltip.left="(item.action as DropdownAction).tooltip || undefined"
            class="app-dropdown__content flex items-center gap-2"
            :data-tooltip="(item.action as DropdownAction).tooltip || undefined"
          >
            <AppIcon
              v-if="(item.action as DropdownAction).icon"
              :name="(item.action as DropdownAction).icon ?? ''"
              :size="16"
            />
            <span>{{ item.label }}</span>
          </span>
        </a>
      </template>
    </Menu>
  </div>
</template>

<style scoped>
.app-dropdown__trigger {
  display: inline-flex;
  align-items: center;
  padding: 0.25rem;
  color: var(--p-text-color);
  background: transparent;
  border: 0;
  cursor: pointer;
}
</style>

<style>
/* The panel is teleported to <body>: unscoped, prefixed rules. */
.app-dropdown__item--primary {
  color: var(--p-primary-500);
}
.app-dropdown__item--success {
  color: var(--p-green-500);
}
.app-dropdown__item--warning {
  color: var(--p-orange-500);
}
.app-dropdown__item--danger {
  color: var(--p-red-500);
}
.app-dropdown__item--info {
  color: var(--p-cyan-500);
}
/* A disabled item keeps its tooltip (PrimeVue turns pointer events off on the item). */
.p-menu .p-disabled .app-dropdown__content[data-tooltip] {
  pointer-events: auto;
}
.app-dropdown__header-label {
  font-size: 0.857rem;
  font-weight: 600;
  color: var(--p-text-muted-color);
}
</style>
